import React, { useContext } from 'react'

import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ArcadeButton, Button } from '@cash-design-system/react'
import { ClientScenario } from '@cash-web/protos/squareup/franklin/api/client_scenario.pb'
import { SelectionBlocker } from '@cash-web/protos/squareup/franklin/blockers/selection_blocker.pb'
import { SelectionBlockerSupplement } from '@cash-web/protos/squareup/franklin/scenarios.pb'
import { useBlockerResponseHandler } from '@cash-web/shared-blocker-data-access-context'
import { useMutationSelectOption } from '@cash-web/shared-data-access-api/react-query/useMutationSelectOption'
import { ErrorContext } from '@cash-web/shared-data-access-error'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-feature-flags'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'
import { tryAddMagicLinkRedirect } from '@cash-web/shared-util-magic-link'

import { getSelectOptionErrors } from '../../util/statusErrors'
import { BaseBlocker, BaseBlockerFormFields, BlockerProps } from '../BaseBlocker/BaseBlocker'

interface Props extends BlockerProps {
  blocker: SelectionBlocker
  supplement?: SelectionBlockerSupplement
}

interface FormFields extends BaseBlockerFormFields {
  action?: string
  scenario?: ClientScenario
}

// Matches markdown link and link text like [two](https://example.com/2)
// Capture group 1 is the display text. Group 2 is the URI.
const regex = /\[([^[]+)\]\(([^(]+)\)/g
function markdownLinksToHtml(markdown: string): string {
  return markdown.replace(regex, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
}

export function BlockerSelection({ blocker, supplement, skippable }: Props): React.ReactElement {
  const form = useForm<FormFields>()
  const { setValue } = form
  const { t } = useTranslation()
  const { setError } = useContext(ErrorContext)
  const { handleBlockerResponse } = useBlockerResponseHandler()
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')

  const flow = useFlowContext()
  const { mutateAsync: selectOption } = useMutationSelectOption({
    options: {
      clientScenario: flow.active?.clientScenario,
      flowToken: flow.active?.flowToken,
    },
  })

  const instructions = blocker.header_text || blocker.main_text
  const secondaryInstructions = blocker.header_text ? blocker.main_text : ''

  const options = blocker.options || []
  const footerTextHtml = blocker.footer_text ? markdownLinksToHtml(blocker.footer_text) : null

  const primaryButton = blocker.primary_option
  const secondaryButton = blocker.secondary_option

  tryAddMagicLinkRedirect(primaryButton)

  const onSubmit = async ({ action, scenario }: FormFields) => {
    if (scenario && flow.active?.clientScenario !== scenario) {
      setError(t('Sorry, this option is not supported at this time.', { context: 'error message' }))
      return
    }

    await handleBlockerResponse({
      promisedBlockerUpdate: selectOption({
        action,
        transfer_token: flow.active?.transferToken,
        payment_tokens: flow.active?.paymentTokens,
        options: {
          clientScenario: flow.active?.clientScenario,
          flowToken: flow.active?.flowToken,
        },
      }),
      defaultError: t('Sorry, an error occurred and that option was not selected', { context: 'error message' }),
      errors: getSelectOptionErrors(t),
    })
  }

  async function handleSelectOption(action?: string, optionScenario?: ClientScenario) {
    setValue('action', action)
    setValue('scenario', optionScenario)
  }

  const buttons = []
  if (secondaryButton) {
    buttons.push(
      isArcadeMigrationEnabled ? (
        <ArcadeButton
          type="submit"
          prominence="prominent"
          key="selection-blocker-secondary-button"
          onClick={() => handleSelectOption(secondaryButton.action, secondaryButton.client_scenario)}
        >
          {secondaryButton.label}
        </ArcadeButton>
      ) : (
        <Button
          type="submit"
          variant="primary"
          key="selection-blocker-secondary-button"
          onClick={() => handleSelectOption(secondaryButton.action, secondaryButton.client_scenario)}
        >
          {secondaryButton.label}
        </Button>
      )
    )
  }
  if (primaryButton) {
    buttons.push(
      isArcadeMigrationEnabled ? (
        <ArcadeButton
          type="submit"
          prominence="prominent"
          key="selection-blocker-primary-button"
          onClick={() => handleSelectOption(primaryButton.action, primaryButton.client_scenario)}
        >
          {primaryButton.label}
        </ArcadeButton>
      ) : (
        <Button
          type="submit"
          variant="primary"
          key="selection-blocker-primary-button"
          onClick={() => handleSelectOption(primaryButton.action, primaryButton.client_scenario)}
        >
          {primaryButton.label}
        </Button>
      )
    )
  }
  const helpModal = supplement?.additional_help_items ? { helpItems: supplement.additional_help_items } : undefined
  const footer = footerTextHtml ? <p dangerouslySetInnerHTML={{ __html: footerTextHtml }} /> : undefined

  // Note: The `blocker.icon` information is intentionally not being used so that the frontend has
  // more control of what icons get shown when.
  return (
    <BaseBlocker
      mainText={instructions}
      secondaryText={secondaryInstructions}
      footer={footer}
      form={form}
      onSubmit={onSubmit}
      buttons={buttons}
      skippable={skippable}
      helpModal={helpModal}
    >
      {options.map(option =>
        isArcadeMigrationEnabled ? (
          <ArcadeButton
            type="submit"
            prominence="standard"
            key={option.label}
            onClick={() => handleSelectOption(option.action, option.client_scenario)}
          >
            {option.label}
          </ArcadeButton>
        ) : (
          <Button
            type="submit"
            variant="secondary"
            key={option.label}
            onClick={() => handleSelectOption(option.action, option.client_scenario)}
          >
            {option.label}
          </Button>
        )
      )}
    </BaseBlocker>
  )
}
